import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { RequestCancelAxios } from 'shared/api/lib'
import { ContactsApi } from 'entities/Contacts'
import type { IResponseContactDuplicate } from 'entities/Contacts/api/types'
import { RowContactDuplicate } from 'entities/Contacts/model/Table/RowContactDuplicate'
import { ContactsMergeModalStore } from 'widgets/ContactsMergeModal'
import { type CleanupPageStore } from 'pages/contacts/pages/cleanup/store/cleanupPageStore'

export class ContactsDuplicateTableStore {
  private _itemsMap: Map<number, RowContactDuplicate> = new Map()
  private _disposePagination: IReactionDisposer | null = null
  private _disposeTotal: IReactionDisposer | null = null
  private _contactsMergeModalStore = new ContactsMergeModalStore()
  private readonly _tableStore: TableStore<RowContactDuplicate>
  private _cancelRequest = new RequestCancelAxios()
  private _total = 0

  constructor(private _cleanupPageStore: CleanupPageStore) {
    makeAutoObservable(this)

    this._contactsMergeModalStore.setConfig({
      onMergeAction: () => {
        const successDuplicateMergeHandler = (id: number) => {
          this.successMergeHandler(id)
          this._cleanupPageStore.subtractDuplicateCount()
        }

        this._contactsMergeModalStore.merge(
          successDuplicateMergeHandler,
          this._cleanupPageStore.duplicateCount
        )
      },
    })

    this._tableStore = new TableStore({
      key: 'contacts_duplicate',
      limit: 10,
    })
  }

  get tableStore() {
    return this._tableStore
  }

  get contactsMergeModalStore() {
    return this._contactsMergeModalStore
  }

  get loading() {
    return this._tableStore.loading
  }

  get total() {
    return this._total
  }

  get noResults() {
    return this.total === 0
  }

  get list() {
    return Array.from(this._itemsMap.values())
  }

  init = () => {
    this.reactionPagination()
    this.reactionTotal()
    this.load()
  }

  resetPaginationPage = () => {
    this._tableStore.setPage(1)
    this._tableStore.setLimit(10)
  }

  dispose = () => {
    this._tableStore.dispose()
    this._itemsMap.clear()
    this._disposePagination?.()
    this._disposeTotal?.()
    this.resetPaginationPage()
    this._cancelRequest.reset()
  }

  changeTotal = (total: number) => {
    this._total = total
    this._tableStore.setTotal(total)
  }

  get params() {
    return this._tableStore.params
  }

  load = async () => {
    if (this.loading) return

    try {
      this._cancelRequest.init()

      this._tableStore.setLoading(true)

      const { data } = await ContactsApi.getContactsDuplicate(this.params, {
        cancelToken: this._cancelRequest.token,
      })
      const results = data.data || []
      const total = data.total || 0

      this.addItems(results)

      runInAction(() => {
        this.changeTotal(total)
      })
    } catch (e) {
      logger.error(e)
    } finally {
      this._tableStore.setLoading(false)
    }
  }

  deleteLocallyAfterMerge = (id: number) => {
    this._itemsMap.delete(id)
  }

  decreaseDuplicateCount = (row: RowContactDuplicate) => {
    row.setCount(row.count - 1)
  }

  successMergeHandler = (id: number) => {
    const item = this._itemsMap.get(id)

    if (!item) return

    if (item.count - 1 === 1) {
      this.deleteLocallyAfterMerge(id)
      this.changeTotal(this.total - 1)
    } else {
      this.decreaseDuplicateCount(item)
    }
  }

  addItem = (row: RowContactDuplicate) => {
    this._itemsMap.set(row.id, row)
  }

  addItems = (contacts: IResponseContactDuplicate[]) => {
    this._itemsMap.clear()

    contacts.forEach((item) => {
      const row = new RowContactDuplicate({
        id: parseInt(item.formatted_number.replace(/\D/g, '')),
        ...item,
      })

      this.addItem(row)
    })
  }

  reactionPagination = () => {
    this._disposePagination?.()
    this._disposePagination = reaction(
      () => this.params,
      () => {
        this.load()
      }
    )
  }

  reactionTotal = () => {
    this._disposeTotal?.()
    this._disposeTotal = reaction(
      () => this.total,
      (value) => {
        this._contactsMergeModalStore.setTotal(value)
      }
    )
  }
}
